/**
 * A technical token that must read left to right inside Persian prose.
 *
 * Every phone number, token prefix, client id, tracking id, service code and
 * URL in this portal is Latin text sitting in a right-to-left paragraph. Left
 * to the bidi algorithm, the neutral characters around it — `+`, `-`, spaces,
 * and any adjacent digits — get resolved against the *paragraph* direction,
 * and the run comes apart: `+989121234567` next to a number renders as
 * `13+9891*****10`, which is not a phone number anybody has.
 *
 * The fix is isolation, not direction. `<bdi>` is exactly this element:
 * `unicode-bidi: isolate` in every UA stylesheet, which makes the content one
 * opaque neutral run to the text around it and resolves its insides on their
 * own. `dir="ltr"` then fixes what "on their own" means.
 *
 * Both are HTML, and that is deliberate. The portal's emotion cache runs
 * `stylis-plugin-rtl`, which rewrites CSS for RTL — including flipping
 * `direction: ltr` to `direction: rtl`. Every `sx={{ direction: 'ltr' }}` in
 * this codebase was therefore doing the opposite of what it said, which is
 * where the broken phone number came from. The plugin cannot touch an HTML
 * attribute, and it leaves `unicode-bidi` alone.
 *
 * So: no `direction` in `sx`, here or anywhere. Use this.
 */
import Box from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'
import type { ElementType, ReactNode } from 'react'

export function Ltr({
  children,
  mono = false,
  component = 'bdi',
  sx,
  ...rest
}: {
  children: ReactNode
  /** Monospace, for anything somebody may have to compare character by character. */
  mono?: boolean
  component?: ElementType
  sx?: SxProps<Theme>
} & Record<string, unknown>) {
  return (
    <Box
      component={component}
      dir="ltr"
      sx={[
        {
          // Isolation is the whole point. Stated rather than inherited from
          // the UA stylesheet so that a `component` override cannot lose it.
          unicodeBidi: 'isolate',
          ...(mono ? { fontFamily: 'monospace' } : {}),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {children}
    </Box>
  )
}
